import { MessageFilled } from "@ant-design/icons";
import { ChangeEvent, FC, memo, useCallback, useMemo } from "react";

import FlexContainer from "@/components/FlexContainer";
import { StatusIndicator } from "@/components/Status/StatusIndicator";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";

import {
    InputStatus,
    StyledCommentIndicator,
    StyledFacingsInput,
    StyledTaskFacings,
} from "./Styled";

interface FacingTaskPayload {
    amount: number | null;
    comment?: string;
}

interface FacingTaskProps {
    finGroup: string;
    plan: number | null;
    value: number | null;
    isActive: boolean;
    payload?: FacingTaskPayload;
    onChangeActive: (id: string) => void;
    onChangeFact: (finGroup: string, value: number | null) => void;
}

export const FacingTask: FC<FacingTaskProps> = memo(
    ({
        finGroup,
        plan,
        value,
        isActive,
        payload,
        onChangeActive,
        onChangeFact,
    }) => {
        const hasComment = useMemo(
            () => !!payload?.comment,
            [payload?.comment],
        );
        const inputStatus: InputStatus = useMemo(() => {
            if (value === null || plan === null) return "neutral";
            if (value > plan) return "more";
            if (value < plan) return "less";

            return "neutral";
        }, [value, plan]);

        const selectTask = useCallback(() => {
            onChangeActive(finGroup);
        }, [finGroup, onChangeActive]);

        const handleChange = useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                const inputValue = e.target.value;
                const newValue = inputValue === "" ? null : Number(inputValue);
                onChangeFact(finGroup, newValue);
            },
            [finGroup, onChangeFact],
        );

        return (
            <StyledTaskFacings $isActive={isActive} onClick={selectTask}>
                <FlexContainer
                    flexDirection="column"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                >
                    <PrimaryText>Кі-сть фейсів {finGroup}</PrimaryText>
                    <FlexContainer gap="6px">
                        <SecondaryText>План:</SecondaryText>
                        <PrimaryText>{plan === null ? "?" : plan}</PrimaryText>
                    </FlexContainer>
                </FlexContainer>

                <FlexContainer>
                    {hasComment && (
                        <StyledCommentIndicator>
                            <StatusIndicator type="warning">
                                <MessageFilled />
                            </StatusIndicator>
                        </StyledCommentIndicator>
                    )}

                    <StyledFacingsInput
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min={0}
                        value={value ?? ""}
                        onChange={handleChange}
                        $status={inputStatus}
                        onKeyDown={(e) => {
                            if (["-", "+", "e", ".", ","].includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                    />
                </FlexContainer>
            </StyledTaskFacings>
        );
    },
);

FacingTask.displayName = "FacingTask";
